import React, { useState, useEffect } from "react";
import { db } from "../firebaseConfig";
import { collection, getDocs, addDoc, serverTimestamp } from "firebase/firestore";
import { useAuth } from "../context/AuthContext";
import { DateTime } from "luxon";
import "../pages/BookAppointment.css";
import { useNavigate } from "react-router-dom";

const SUMMARY_DEFAULT = "";
const STRIPED_RED = "striped-red";

function BookAppointment() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // --- Client list + auto-fields ---
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedClientId, setSelectedClientId] = useState("");

  // We’ll reuse appointmentContacts for "Default Contact"
  const [appointmentContacts, setAppointmentContacts] = useState("");

  // Auto-populated bellNotes + static clientID
  const [bellNotes, setBellNotes] = useState("");
  const [clientID, setClientID] = useState("");

  // --- Appointment fields ---
  const [appointmentDate, setAppointmentDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [autoAddress, setAutoAddress] = useState("");
  const [overrideAddress, setOverrideAddress] = useState("");
  const [isOverride, setIsOverride] = useState(false);
  const [notes, setNotes] = useState("");
  const [extraEquipment, setExtraEquipment] = useState("");
  const [fieldBuildingLocation, setFieldBuildingLocation] = useState("");
  const [alternateShipTo, setAlternateShipTo] = useState("");
  const [productionNotes, setProductionNotes] = useState("");
  const [orderFormNotes, setOrderFormNotes] = useState("");
  const [bannerInfo, setBannerInfo] = useState("");
  const [color, setColor] = useState("#dedede");
  const [summary, setSummary] = useState("");

  const finalAddress = isOverride ? overrideAddress : autoAddress;

  // Log creation to activity_logs (unchanged)…
  const logAppointmentCreation = async (appointmentId, clientName, appointmentDateStr) => {
    try {
      if (!currentUser?.email) return;
      const logEntry = {
        timestamp: serverTimestamp(),
        user: currentUser.email,
        action: "Created Appointment",
        appointmentId,
        clientName: clientName || "Unknown Client",
        appointmentDate: appointmentDateStr || "Not Set",
        changedFields: {},
        isDeleted: false,
      };
      await addDoc(collection(db, "activity_logs"), logEntry);
    } catch (error) {
      console.error("Logging failed:", error);
    }
  };

  // Fetch clients + defaultContact & bellNotes
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const snap = await getDocs(collection(db, "clients"));
        const list = snap.docs
          .map(d => ({
            id: d.id,
            name: d.data().name,
            address: d.data().address || "",
            shippingAddress: d.data().shippingAddress || "",
            defaultContact: d.data().defaultContact || "",
            bellNotes: d.data().bellNotes || "",
            clientID: d.data().clientID || "",
            notes: d.data().notes || ""          // ← NEW
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setClients(list);
      } catch (e) {
        console.error("Error fetching clients:", e);
      }
    };
    fetchClients();
  }, []);

  // When user picks a client, seed all auto-fields
  const handleClientChange = e => {
    const id = e.target.value;
    setSelectedClientId(id);
    const c = clients.find(x => x.id === id);
    if (c) {
      setSelectedClient(c.name);
      setAutoAddress(c.address);
      setAlternateShipTo(c.shippingAddress);
      setAppointmentContacts(c.defaultContact); // <- "Default Contact"
      setBellNotes(c.bellNotes);
      setClientID(c.clientID);
      setNotes(c.notes);
      // reset address override
      setOverrideAddress("");
      setIsOverride(false);
    }
  };

  // Submit new appointment
  const handleSubmit = async e => {
    e.preventDefault();
    if (!selectedClientId || !appointmentDate || !startTime || !endTime || !finalAddress) {
      alert("Please fill in all required fields.");
      return;
    }

    try {
      const startISO = DateTime.fromISO(`${appointmentDate}T${startTime}`)
        .toUTC().toISO();
      const endISO = DateTime.fromISO(`${appointmentDate}T${endTime}`)
        .toUTC().toISO();

      const newAppt = {
        client: selectedClient,
        clientId: selectedClientId,
        clientID,
        // appointmentContacts now holds your "Default Contact" (auto+override)
        appointmentContacts,
        bellNotes,
        appointmentDate: startISO,
        endDate: endISO,
        startTime: startTime,
        endTime: endTime,
        address: finalAddress,
        photographers: ["Unassigned"],
        extraEquipment,
        fieldBuildingLocation,
        alternateShipTo,
        productionNotes,
        notes: notes || "",
        color,
        orderFormNotes,
        bannerInfo,
        summary: summary || SUMMARY_DEFAULT,
      };

      const docRef = await addDoc(collection(db, "appointments"), newAppt);
      await logAppointmentCreation(docRef.id, selectedClient, startISO);
      alert("Appointment booked successfully!");
      
      // clear form
      setSelectedClientId("");
      setSelectedClient("");
      setAppointmentDate("");
      setStartTime("");
      setEndTime("");
      setOverrideAddress("");
      setIsOverride(false);
      setAppointmentContacts("");
      setBellNotes("");
      setClientID("");
      setNotes("");
      setExtraEquipment("");
      setFieldBuildingLocation("");
      setAlternateShipTo("");
      setProductionNotes("");
      setOrderFormNotes("");
      setBannerInfo("");
      setColor("#dedede");
      setSummary("");

      navigate("/scheduler", { state: { appointmentId: docRef.id } });
    } catch (err) {
      console.error("Error booking appointment:", err);
      alert("Failed to book appointment.");
    }
  };

  return (
    <div className="book-appointment-container">
      <h2>Book Appointment</h2>
      <form onSubmit={handleSubmit} className="appointment-form">
        {/* Client */}
        <label>Client:</label>
        <select value={selectedClientId} onChange={handleClientChange} required>
          <option value="">Select Client</option>
          {clients.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>

        {/* Client ID (read-only) */}
        <label>Client ID:</label>
        <input type="text" value={clientID} readOnly />

        {/* Date */}
        <label>Date:</label>
        <input
          type="date"
          value={appointmentDate}
          onChange={e => setAppointmentDate(e.target.value)}
          required
        />

        {/* Start/End */}
        <label>Start Time:</label>
        <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} required />
        <label>End Time:</label>
        <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} required />

        {/* Address */}
        <label>Appointment Location Address:</label>
        <input
          type="text"
          value={isOverride ? overrideAddress : autoAddress}
          disabled={!isOverride}
          onChange={e => setOverrideAddress(e.target.value)}
        />
        <div className="override-container">
          <input
            type="checkbox"
            checked={isOverride}
            onChange={() => setIsOverride(!isOverride)}
          />
          <label>Override Address</label>
        </div>

        {/* Default Contact (built on appointmentContacts) */}
        <label>Default Contact:</label>
        <textarea
          value={appointmentContacts}
          onChange={e => setAppointmentContacts(e.target.value)}
          rows="2"
          style={{ width: "100%" }}
        />

        {/* Extra fields… */}
        <label>Extra Equipment:</label>
        <textarea
          value={extraEquipment}
          onChange={e => setExtraEquipment(e.target.value)}
          rows="3"
          style={{ width: "100%" }}
        />

        <label>Field/Building Location:</label>
        <textarea
          value={fieldBuildingLocation}
          onChange={e => setFieldBuildingLocation(e.target.value)}
          rows="3"
          style={{ width: "100%" }}
        />

        <label>Notes & Schedule:</label>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows="4"
          style={{ width: "100%" }}
        />

        <label>Production Notes:</label>
        <textarea
          value={productionNotes}
          onChange={e => setProductionNotes(e.target.value)}
          rows="4"
          style={{ width: "100%" }}
        />

        <label>Ship-To Address:</label>
        <textarea
          value={alternateShipTo}
          onChange={e => setAlternateShipTo(e.target.value)}
          rows="3"
          style={{ width: "100%" }}
        />

        {/* Bell Notes */}
        <label>Bell Notes:</label>
        <textarea
          value={bellNotes}
          onChange={e => setBellNotes(e.target.value)}
          rows="3"
          style={{ width: "100%" }}
        />

        <label>Banner Info:</label>
        <textarea value={bannerInfo} onChange={event=>setBannerInfo(event.target.value)} rows="5" style={{width:"100%"}} />

        <label>Order Form Notes:</label>
        <textarea
          value={orderFormNotes}
          onChange={e => setOrderFormNotes(e.target.value)}
          rows="4"
          style={{ width: "100%" }}
        />

        <label>Summary:</label>
        <textarea
          value={summary}
          onChange={e => setSummary(e.target.value)}
          rows="3"
          style={{ width: "100%" }}
        />

        <label>Appointment Color:</label>
        <select value={color} onChange={e => setColor(e.target.value)} required>
          <option value="#dedede">Grey</option>
          <option value="#ff5733">Red</option>
          <option value={STRIPED_RED}>Striped Red</option>
          <option value="#357a38">Green</option>
          <option value="#33b5ff">Blue</option>
          <option value="#000000">Black</option>
          <option value="#800080">Purple</option>        
          <option value="#FFA500">Orange</option>       
          <option value="#FFD700">Yellow</option>        
        </select>

        <button type="submit" className="submit-button">
          Book Appointment
        </button>
      </form>
    </div>
  );
}

export default BookAppointment;
